import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiChevronDown, FiChevronRight, FiCopy, FiCheck } = FiIcons;

function ApiLogPanel({ logs }) {
  const [filter, setFilter] = useState('all');
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [copySuccess, setCopySuccess] = useState('');
  const logEndRef = useRef(null);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'success', label: 'Success' },
    { id: 'error', label: 'Error' },
    { id: 'ncb', label: 'NCB' },
    { id: 'dropbox', label: 'Dropbox' },
    { id: 'openai', label: 'OpenAI' }
  ];

  const filteredLogs = logs.filter((log) => {
    switch (filter) {
      case 'success':
        return log.type === 'success' || (log.status && log.status < 400);
      case 'error':
        return log.type === 'error' || (log.status && log.status >= 400);
      case 'ncb':
        return log.api === 'ncb';
      case 'dropbox':
        return log.api === 'dropbox';
      case 'openai':
        return log.api === 'openai';
      default:
        return true;
    }
  });

  const getFilterCount = (filterId) => {
    return logs.filter((log) => {
      switch (filterId) {
        case 'success':
          return log.type === 'success' || (log.status && log.status < 400);
        case 'error':
          return log.type === 'error' || (log.status && log.status >= 400);
        case 'ncb':
          return log.api === 'ncb';
        case 'dropbox':
          return log.api === 'dropbox';
        case 'openai':
          return log.api === 'openai';
        default:
          return true;
      }
    }).length;
  };

  const toggleExpanded = (logId) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(`${type}-copied`);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getMethodColor = (method) => {
    switch (method?.toUpperCase()) {
      case 'GET': return 'bg-green-100 text-green-800';
      case 'POST': return 'bg-blue-100 text-blue-800';
      case 'PUT': return 'bg-yellow-100 text-yellow-800';
      case 'DELETE': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    if (status >= 200 && status < 300) return 'bg-green-100 text-green-800';
    if (status >= 400 && status < 500) return 'bg-yellow-100 text-yellow-800';
    if (status >= 500) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getApiColor = (api) => {
    switch (api) {
      case 'ncb': return 'bg-blue-100 text-blue-800';
      case 'dropbox': return 'bg-green-100 text-green-800';
      case 'openai': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border h-full flex flex-col">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">API Logs</h2>
          <span className="text-sm text-gray-500">
            {filteredLogs.length} of {logs.length} logs
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {filters.map((filterOption) => {
            const count = getFilterCount(filterOption.id);
            return (
              <button 
                key={filterOption.id} 
                onClick={() => setFilter(filterOption.id)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  filter === filterOption.id 
                    ? 'bg-primary-500 text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filterOption.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-12">
            <SafeIcon icon={FiClock} className="text-gray-400 text-3xl mx-auto mb-4" />
            <p className="text-gray-500">No logs yet. Execute an API request to see logs here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredLogs.map((log) => {
              const isExpanded = expandedLogs.has(log.id);
              const timestamp = new Date(log.timestamp);
              
              return (
                <div key={log.id} className="border rounded-lg overflow-hidden">
                  <button 
                    onClick={() => toggleExpanded(log.id)}
                    className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between"
                  >
                    <div className="flex items-center space-x-3">
                      <SafeIcon 
                        icon={isExpanded ? FiChevronDown : FiChevronRight} 
                        className="text-gray-400" 
                      />
                      
                      {log.api && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getApiColor(log.api)}`}>
                          {log.api.toUpperCase()}
                        </span>
                      )}
                      
                      {log.method && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getMethodColor(log.method)}`}>
                          {log.method}
                        </span>
                      )}
                      
                      {log.status && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(log.status)}`}>
                          {log.status}
                        </span>
                      )}
                      
                      <span className="text-sm text-gray-900 truncate">
                        {log.url || log.message}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-xs text-gray-500">
                      {log.duration && <span>{log.duration}ms</span>}
                      <span>{format(timestamp, 'HH:mm:ss')}</span>
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="p-4 space-y-4">
                      {log.request && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700">Request</h4>
                            <button 
                              onClick={() => copyToClipboard(JSON.stringify(log.request, null, 2), 'request')}
                              className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700"
                            >
                              <SafeIcon icon={copySuccess === 'request-copied' ? FiCheck : FiCopy} />
                              <span>{copySuccess === 'request-copied' ? 'Copied!' : 'Copy'}</span>
                            </button>
                          </div>
                          <pre className="json-syntax">
                            {typeof log.request === 'string' ? log.request : JSON.stringify(log.request, null, 2)}
                          </pre>
                        </div>
                      )}

                      {log.response && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-medium text-gray-700">Response</h4>
                            <button 
                              onClick={() => copyToClipboard(JSON.stringify(log.response, null, 2), 'response')}
                              className="flex items-center space-x-1 text-xs text-gray-500 hover:text-gray-700"
                            >
                              <SafeIcon icon={copySuccess === 'response-copied' ? FiCheck : FiCopy} />
                              <span>{copySuccess === 'response-copied' ? 'Copied!' : 'Copy'}</span>
                            </button>
                          </div>
                          <pre className="json-syntax">
                            {typeof log.response === 'string' ? log.response : JSON.stringify(log.response, null, 2)}
                          </pre>
                        </div>
                      )}

                      {log.error && (
                        <div>
                          <h4 className="text-sm font-medium text-red-700 mb-2">Error</h4>
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-red-700 text-sm">{log.error}</p>
                          </div>
                        </div>
                      )}

                      {log.headers && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">Headers</h4>
                          <pre className="json-syntax">
                            {JSON.stringify(log.headers, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={logEndRef} />
          </div>
        )}
      </div>
    </div>
  );
}

export default ApiLogPanel;